import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import appConfig from "../../appConfig";
import { fetchSecure, getToken } from "../../services/apiService";
import SectionView from "./SectionView";
import { TestSetPanel, useTestSetNav } from "./TestSetPanel";
import { SOURCE_STYLES, issueCounts } from "./bomView";
import {
  CodeStringLine,
  CopyLinkButton,
  DownloadStatus,
  ErrorCard,
  IssueBadge,
  LoadingCard,
  SectionTabs,
  ViewSwitch,
  runDetails,
  useDocumentTitle,
  useDownload,
  useViewerParams,
} from "./viewerParts";
import "../bomScheduler/FileUpload.css";
import "./BomViewer.css";

// One saved BOM (a Raw output of any source) in its own page, opened from the inputs dialog in a new tab
export default function BomViewerPage() {
  const { outputId } = useParams();
  const { instance, accounts } = useMsal();
  const [params, setParam] = useViewerParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setState((prev) => ({ view: prev.view, loading: true }));
      const token = await getToken(instance, accounts);
      const response = token ? await fetchSecure(token, `${appConfig.apiUrl}outputs/${outputId}/bom`) : null;
      if (cancelled) return;
      if (response?.status === "OK") setState({ view: response.data });
      else setState({ error: response?.message || "The BOM could not be loaded. Check your connection and try again." });
    };
    load();
    return () => {
      cancelled = true;
    };
    // accounts changes identity on every render of MsalProvider; the id is what matters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputId]);

  const view = state.view;
  useDocumentTitle(view ? `${view.fileName} · BOM viewer` : "BOM viewer");
  const nav = useTestSetNav({ uploadId: view?.input.uploadId, inputId: view?.input.inputId, kind: "bom", environment: view?.environment });

  if (state.loading && !view) return <LoadingCard text="Loading the BOM..." />;
  if (state.error) return <ErrorCard text={state.error} />;

  const issueTab = view.sections.find((s) => issueCounts(s).notFound + issueCounts(s).error > 0);
  const sectionName = params.get("tab");
  const section =
    view.sections.find((s) => s.name === sectionName) || (params.get("issues") === "1" && issueTab) || view.sections[0];

  return (
    <div className="bom-viewer text-start">
      <BomHeader view={view} instance={instance} accounts={accounts} nav={nav} />
      <div className="bom-viewer__layout">
        <TestSetPanel nav={nav} />
        <div className="bom-viewer__main" aria-busy={state.loading ? "true" : undefined}>
          <SectionTabs
            sections={view.sections}
            current={section}
            onSelect={(s) => setParam("tab", s === view.sections[0] ? null : s.name)}
            badge={(s) => (
              <>
                <span className="bom-viewer__count">{s.rows.length.toLocaleString()}</span>
                <IssueBadge counts={issueCounts(s)} />
              </>
            )}
          />
          {section ? (
            <SectionView key={`${view.outputId}-${section.name}`} section={section} source={view.source} params={params} setParam={setParam} />
          ) : (
            <div className="card border-0 shadow-sm"><div className="card-body text-muted">The file has no tabs.</div></div>
          )}
        </div>
      </div>
    </div>
  );
}

function BomHeader({ view, instance, accounts, nav }) {
  const [download, downloadFile] = useDownload(instance, accounts);
  const input = view.input;
  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-body p-3 p-md-4">
        <Link to="/" className="small">&larr; Back to BOM Compare</Link>
        <div className="d-flex flex-wrap align-items-start gap-3 mt-2">
          <div className="me-auto">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className={`bom-source ${SOURCE_STYLES[view.source] || ""}`}>{view.environmentLabel}</span>
              <h3 className="upload-panel__title mb-0">{input.testSet}</h3>
            </div>
            <div className="bom-viewer__meta">{runDetails(input, view.source).join(" · ")}</div>
            {view.source === "Infor" && <CodeStringLine input={input} />}
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <ViewSwitch links={view.links} current={{ kind: "bom", id: view.outputId }} />
            <CopyLinkButton />
            <DownloadStatus status={download} />
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => downloadFile(view.outputId, view.fileName)} disabled={download === "Downloading..."}>
              Download Excel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
